import { useState, useEffect, useRef } from 'react'
import { useIntelData } from '../../contexts/IntelDataContext'
import { Spinner } from '../../components/Spinner'
import PredictionFunnel, { type FunnelStage, type FunnelView } from '../../components/intel/PredictionFunnel'
import type { StageLiveness } from '../../components/intel/FunnelStageLiveness'
import FunnelDrilldownPanel, { type Prediction as FunnelPrediction } from '../../components/intel/FunnelDrilldownPanel'
import ScoreboardCoverageCell from '../../components/intel/ScoreboardCoverageCell'
import { computeScoreboardTotals, type ScoreboardRow } from './scoreboardTotals'

const TOPICS = ['gold', 'silver', 'usd', 'equities', 'crypto', 'geopolitics', 'macro', 'recession', 'war', 'other']

interface Influencer {
  id: number
  name: string
  prediction_count?: number | string
}

interface FunnelResponse {
  as_of: string
  total: number
  stages: FunnelStage[]
}

interface FunnelLivenessItem extends StageLiveness {
  key: string
  canonical_label?: string | null
}

function AccuracyBadge({ correct, incorrect, partial }: { correct: number; incorrect: number; partial: number }) {
  const scored = correct + incorrect + partial
  if (scored === 0) return <span className="text-gray-500 text-xs">--</span>
  const pct = Math.round(correct / scored * 100)
  return (
    <span className={`text-xs font-medium ${pct >= 60 ? 'text-emerald-600' : pct >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
      {pct}%
    </span>
  )
}

export default function Scoreboard() {
  const intel = useIntelData()
  const [data, setData] = useState<ScoreboardRow[]>((intel.scoreboardCache?.data || []) as ScoreboardRow[])
  const [influencers, setInfluencers] = useState<Influencer[]>((intel.influencersCache?.data || []) as Influencer[])
  const [loading, setLoading] = useState(!intel.scoreboardCache?.data)

  // CHO-581: prediction attrition funnel
  const [funnel, setFunnel] = useState<FunnelResponse | null>((intel.funnelCache?.data as FunnelResponse | null) ?? null)
  const [funnelLoading, setFunnelLoading] = useState(!intel.funnelCache?.data)
  const [funnelError, setFunnelError] = useState<string | null>(null)
  const [selectedStage, setSelectedStage] = useState<{ stage: FunnelStage; view: FunnelView } | null>(null)
  const [drilldownRows, setDrilldownRows] = useState<FunnelPrediction[]>([])
  const [drilldownTotal, setDrilldownTotal] = useState(0)
  const [drilldownLoading, setDrilldownLoading] = useState(false)
  const [drilldownError, setDrilldownError] = useState<string | null>(null)
  const inFlightKey = useRef<string | null>(null)

  useEffect(() => {
    if (!intel.isStale(intel.scoreboardCache?.timestamp)) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    Promise.all([
      fetch('/api/intel/stats/scoreboard').then(r => r.ok ? r.json() : []),
      intel.isStale(intel.influencersCache?.timestamp)
        ? fetch('/api/intel/influencers').then(r => r.ok ? r.json() : [])
        : Promise.resolve(intel.influencersCache.data)
    ]).then(([scoreboardData, influencersData]: [ScoreboardRow[], Influencer[]]) => {
      setData(scoreboardData)
      setInfluencers(influencersData)
      intel.cacheScoreboard(scoreboardData)
      intel.cacheInfluencers(influencersData)
    }).catch(() => {}).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!intel.isStale(intel.funnelCache?.timestamp)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFunnelLoading(false)
      return
    }
    setFunnelLoading(true)
    setFunnelError(null)
    const funnelPromise = fetch('/api/intel/stats/prediction-funnel')
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${r.status}`)
        }
        return r.json() as Promise<FunnelResponse>
      })
    const livenessPromise = fetch('/api/health/funnel')
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${r.status}`)
        }
        return r.json() as Promise<{ stages: FunnelLivenessItem[] }>
      })
      .catch((err: Error) => {
        // A failed liveness fetch must not blank the funnel.
        console.error('[funnel-liveness] failed to load liveness:', err)
        return null
      })
    Promise.all([funnelPromise, livenessPromise])
      .then(([funnelData, livenessData]) => {
        const livenessByKey = new Map<string, FunnelLivenessItem>(
          (livenessData?.stages ?? []).map((s) => [s.key, s])
        )
        const mergedStages = funnelData.stages.map((stage) => {
          const liveness = livenessByKey.get(stage.key)
          return {
            ...stage,
            label: liveness?.canonical_label ?? stage.label,
            liveness,
          }
        })
        const merged = { ...funnelData, stages: mergedStages }
        setFunnel(merged)
        intel.cacheFunnel(merged)
      })
      .catch((err: Error) => {
        setFunnelError(err.message || 'Failed to load funnel')
      })
      .finally(() => setFunnelLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDrilldown = (stage: FunnelStage, view: FunnelView) => {
    inFlightKey.current = `${stage.key}:${view}`
    setDrilldownLoading(true)
    setDrilldownError(null)
    const params = new URLSearchParams({
      paginated: '1',
      limit: '50',
      offset: '0',
      funnel_stage: stage.key,
      funnel_view: view,
    })
    fetch(`/api/intel/predictions?${params}`)
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}))
          throw new Error(body.error || `HTTP ${r.status}`)
        }
        return r.json()
      })
      .then((res: { data: FunnelPrediction[]; total: number }) => {
        if (inFlightKey.current !== `${stage.key}:${view}`) return
        setDrilldownRows(res.data)
        setDrilldownTotal(res.total)
      })
      .catch((err: Error) => {
        if (inFlightKey.current !== `${stage.key}:${view}`) return
        setDrilldownError(err.message || 'Failed to load rows')
      })
      .finally(() => {
        if (inFlightKey.current === `${stage.key}:${view}`) {
          setDrilldownLoading(false)
        }
      })
  }

  const handleStageClick = (stage: FunnelStage, view: FunnelView) => {
    if (selectedStage?.stage.key === stage.key && selectedStage.view === view) {
      setSelectedStage(null)
      return
    }
    setSelectedStage({ stage, view })
    setDrilldownRows([])
    setDrilldownTotal(0)
    fetchDrilldown(stage, view)
  }

  const handleViewChange = (view: FunnelView) => {
    if (!selectedStage || selectedStage.view === view) return
    handleStageClick(selectedStage.stage, view)
  }

  const handleCloseDrilldown = () => {
    setSelectedStage(null)
    inFlightKey.current = null
  }

  const totals = computeScoreboardTotals(
    data,
    influencers.map(i => ({ id: i.id, name: i.name, prediction_count: i.prediction_count })),
  )

  const grid: Record<number, Record<string, ScoreboardRow>> = {}
  for (const row of data) {
    if (!grid[row.influencer_id]) grid[row.influencer_id] = {}
    grid[row.influencer_id][row.topic] = row
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold mb-1 text-gray-900 dark:text-gray-100">Prediction Scoreboard</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Extracted = all predictions on record. In scoring window = resolve window elapsed. Accuracy = correct / evaluated predictions. Pending = window not yet elapsed.
        </p>

        <PredictionFunnel
          stages={funnel?.stages ?? []}
          total={funnel?.total ?? 0}
          asOf={funnel?.as_of ?? new Date().toISOString()}
          loading={funnelLoading}
          error={funnelError}
          onStageClick={handleStageClick}
          selectedStage={selectedStage}
        />
      </div>

      {loading ? (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg h-[400px] flex flex-col items-center justify-center gap-3">
          <Spinner size={32} />
          <span className="text-sm text-gray-500 dark:text-gray-400">Loading scoreboard...</span>
        </div>
      ) : influencers.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
          No KOLs tracked yet.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800">
                <th className="text-left py-3 px-3 font-semibold text-gray-900 dark:text-gray-100">KOL</th>
                <th className="py-3 px-3 font-semibold text-center text-gray-900 dark:text-gray-100">Extracted</th>
                <th className="py-3 px-3 font-semibold text-center text-gray-900 dark:text-gray-100">In scoring window</th>
                <th className="py-3 px-3 font-semibold text-center text-gray-900 dark:text-gray-100">Evaluated</th>
                <th className="py-3 px-3 font-semibold text-center text-gray-900 dark:text-gray-100">Accuracy</th>
                {TOPICS.map(t => (
                  <th key={t} className="hidden sm:table-cell py-3 px-2 font-semibold capitalize text-xs text-center text-gray-900 dark:text-gray-100">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {influencers.map(inf => (
                <tr key={inf.id} className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="py-3 px-3 font-medium text-gray-900 dark:text-gray-100">{inf.name}</td>
                  <td className="py-3 px-3 text-center text-gray-700 dark:text-gray-300">{totals[inf.id]?.extracted || 0}</td>
                  <td className="py-3 px-3">
                    <ScoreboardCoverageCell inWindow={totals[inf.id]?.inWindow || 0} extracted={totals[inf.id]?.extracted || 0} />
                  </td>
                  <td className="py-3 px-3 text-center">
                    <span className="text-gray-700 dark:text-gray-300">{totals[inf.id]?.scored || 0}</span>
                    {totals[inf.id]?.unverified > 0 && (
                      <span className="text-gray-500 dark:text-gray-400 text-xs ml-1" title="Pending evaluation (window not elapsed)">
                        +{totals[inf.id].unverified} pending
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-center">
                    {totals[inf.id]?.pct != null ? (
                      <span className={`font-semibold ${totals[inf.id].pct >= 60 ? 'text-emerald-600' : totals[inf.id].pct >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {totals[inf.id].pct}%
                      </span>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">--</span>
                    )}
                  </td>
                  {TOPICS.map(t => {
                    const cell = grid[inf.id]?.[t]
                    if (!cell) return <td key={t} className="hidden sm:table-cell py-3 px-2 text-center text-gray-400 dark:text-gray-500">-</td>
                    return (
                      <td
                        key={t}
                        className="hidden sm:table-cell py-3 px-2 text-center"
                        title={`${cell.total_predictions} resolved ${t} predictions — topic columns count the scoring window, not all extracted predictions`}
                      >
                        <div className="text-xs text-gray-500 dark:text-gray-400">{cell.total_predictions}</div>
                        <AccuracyBadge correct={cell.correct} incorrect={cell.incorrect} partial={cell.partial} />
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-800">
            Topic columns count predictions in the scoring window, not all extracted predictions.
          </p>
        </div>
      )}

      {selectedStage && (
        <FunnelDrilldownPanel
          stage={selectedStage.stage}
          view={selectedStage.view}
          rows={drilldownRows}
          total={drilldownTotal}
          loading={drilldownLoading}
          error={drilldownError}
          onClose={handleCloseDrilldown}
          onViewChange={handleViewChange}
        />
      )}
    </div>
  )
}
